import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FiveCrownsGame from '../components/FiveCrownsGame';
import Phase10Game from '../components/Phase10Game';

export default function HistoricalGameView() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const res = await fetch(`${window.API_URL}/api/scores/session/${sessionId}`);
      if (!res.ok) {
        throw new Error('Session not found');
      }
      const data = await res.json();
      setSession(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Convert historical session data to the format expected by game components
  const convertToActiveGameFormat = () => {
    if (!session) return null;

    // Build scores object keyed by user_id
    const scores = {};
    session.players?.forEach(player => {
      scores[player.user_id] = player.round_scores || [];
    });

    // Build player order from existing players
    const playerOrder = session.players?.map(p => p.user_id) || [];

    return {
      id: `historical-${sessionId}`,
      game_id: session.game_id,
      game_name: session.game_name,
      slug: session.slug,
      emoji: session.emoji,
      players: session.players?.map((p, idx) => ({
        user_id: p.user_id,
        name: p.name,
        avatar_color: p.avatar_color,
        turn_order: idx,
        is_dealer: idx === 0
      })) || [],
      game_state: {
        scores,
        playerOrder,
        currentRound: session.slug === 'five-crowns' ? 10 : 9, // Last round
        gameLog: []
      }
    };
  };

  const handleUpdateGameState = async (newState) => {
    // In historical view, we need to update the session's round_scores
    try {
      const { scores } = newState;

      // Convert scores object to player updates
      const playerUpdates = session.players.map(player => ({
        user_id: player.user_id,
        round_scores: scores[player.user_id] || []
      }));

      // Update each player's round scores in the backend
      await fetch(`${window.API_URL}/api/scores/session/${sessionId}/update-scores`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ players: playerUpdates })
      });

      // Refresh session data
      fetchSession();
    } catch (error) {
      console.error('Error updating scores:', error);
    }
  };

  const handleEndGame = async (scores) => {
    // Navigate to game over screen with updated scores
    navigate('/game-over', {
      state: {
        gameData: {
          gameName: session.game_name,
          players: scores.map((s, i) => ({
            ...session.players.find(p => p.user_id === s.user_id),
            score: s.score,
            placement: i + 1
          }))
        },
        sessionId: parseInt(sessionId)
      }
    });
  };

  const handleQuitGame = () => {
    // Go back to the history page
    navigate(`/history/${sessionId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-white">Loading game...</p>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 flex flex-col items-center justify-center p-4">
        <p className="text-red-400 mb-4">{error || 'Game not found'}</p>
        <button
          onClick={() => navigate('/')}
          className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg"
        >
          Back to Home
        </button>
      </div>
    );
  }

  const activeGame = convertToActiveGameFormat();

  // Render appropriate game component based on game type
  const GameComponent = session.slug === 'five-crowns' ? FiveCrownsGame :
                        session.slug === 'phase-10' ? Phase10Game :
                        null;

  if (!GameComponent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white mb-4">Game type not supported for review: {session.game_name}</p>
          <button
            onClick={() => navigate(`/history/${sessionId}`)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            Back to History
          </button>
        </div>
      </div>
    );
  }

  return (
    <GameComponent
      activeGame={activeGame}
      gameState={activeGame.game_state}
      onUpdateGameState={handleUpdateGameState}
      onEndGame={handleEndGame}
      onQuitGame={handleQuitGame}
      onEditGame={() => {}} // No-op for historical view
      socket={null}
      isHistoricalView={true}
    />
  );
}
